import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  GoogleMap,
  withScriptjs,
  withGoogleMap,
  Marker,
  Polyline,
} from "react-google-maps";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import "./App.css";
import locationImage from "../../../assets/Location.png";

const Map = ({ paths: propPaths, stops, selectedMachine }) => {
  const [lastLocation, setLastLocation] = useState(null);
  const [centerLat, setCenterLat] = useState(30.7046);
  const [centerLng, setCenterLng] = useState(76.7179);
  const [progress, setProgress] = useState(null);
  const [paths, setPaths] = useState([]);
  const velocity = 27;

  const initialDate = useRef(null);
  const interval = useRef(null);

  const icon1 = {
    url: "https://images.vexels.com/media/users/3/154573/isolated/preview/bd08e000a449288c914d851cb9dae110-hatchback-car-top-view-silhouette-by-vexels.png",
    scaledSize: new window.google.maps.Size(40, 40),
    anchor: new window.google.maps.Point(20, 20),
    scale: 0.7,
  };

  const locationIcon = {
    url: locationImage,
    scaledSize: new window.google.maps.Size(40, 40),
    anchor: new window.google.maps.Point(20, 20),
    scale: 0.7,
  };

  useEffect(() => {
    setLastLocation(null);
    const fetchLastData = async () => {
      try {
        const response = await fetch(
          "https://argriapi.techexposys.com/machine-tracking/current-coordinates",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              topic: "tes/tracker",
              macaddress: selectedMachine,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        if (data && data.length > 0) {
          setCenterLat(data[0].lat);
          setCenterLng(data[0].lng);
          setLastLocation({
            lat: parseFloat(data[0].lat),
            lng: parseFloat(data[0].lng),
            alt: data[0].altitude,
          });
        } else {
          console.warn("No location data for machine:", selectedMachine);
          setLastLocation(null);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLastLocation(null);
      }
    };

    fetchLastData();
  }, [selectedMachine]);

  useEffect(() => {
    if (!propPaths || propPaths.length === 0) return;
    const newPaths = propPaths.map((coordinates, i, array) => {
      if (i === 0) {
        return { ...coordinates, distance: 0 };
      }
      const { lat: lat1, lng: lng1 } = coordinates;
      const latLong1 = new window.google.maps.LatLng(lat1, lng1);

      const { lat: lat2, lng: lng2 } = array[0];
      const latLong2 = new window.google.maps.LatLng(lat2, lng2);

      const distance =
        window.google.maps.geometry.spherical.computeDistanceBetween(
          latLong1,
          latLong2
        );

      return { ...coordinates, distance };
    });

    setPaths(newPaths);
    setProgress(null);

    if (interval.current) {
      window.clearInterval(interval.current);
      interval.current = null;
    }

    initialDate.current = null;
  }, [propPaths]);

  const getDistance = () => {
    if (!initialDate.current) return 0;
    const differentInTime = (new Date() - initialDate.current) / 1000;
    return differentInTime * velocity;
  };

  const moveObject = () => {
    const distance = getDistance();
    if (!distance) {
      return;
    }

    let progressPoints = paths.filter(
      (coordinates) => coordinates.distance < distance
    );

    const nextLine = paths.find(
      (coordinates) => coordinates.distance > distance
    );

    if (!nextLine) {
      setProgress(progressPoints);
      window.clearInterval(interval.current);
      interval.current = null;
      return;
    }
    const lastLine = progressPoints[progressPoints.length - 1];

    const lastLineLatLng = new window.google.maps.LatLng(
      lastLine.lat,
      lastLine.lng
    );

    const nextLineLatLng = new window.google.maps.LatLng(
      nextLine.lat,
      nextLine.lng
    );

    const totalDistance = nextLine.distance - lastLine.distance;
    const percentage = (distance - lastLine.distance) / totalDistance;

    const position = window.google.maps.geometry.spherical.interpolate(
      lastLineLatLng,
      nextLineLatLng,
      percentage
    );

    mapUpdate();
    setProgress(progressPoints.concat(position));
  };

  const startSimulation = useCallback(() => {
    if (interval.current) {
      window.clearInterval(interval.current);
    }
    setProgress(null);
    initialDate.current = new Date();
    interval.current = window.setInterval(moveObject, 1000);
  }, [paths]);

  const mapUpdate = () => {
    const distance = getDistance();
    if (!distance) {
      return;
    }

    let progressPoints = paths.filter(
      (coordinates) => coordinates.distance < distance
    );

    const nextLine = paths.find(
      (coordinates) => coordinates.distance > distance
    );

    let point1, point2;

    if (nextLine) {
      point1 = progressPoints[progressPoints.length - 1];
      point2 = nextLine;
    } else {
      point1 = progressPoints[progressPoints.length - 2];
      point2 = progressPoints[progressPoints.length - 1];
    }

    if (!point1 || !point2) return;

    const point1LatLng = new window.google.maps.LatLng(point1.lat, point1.lng);
    const point2LatLng = new window.google.maps.LatLng(point2.lat, point2.lng);

    const angle = window.google.maps.geometry.spherical.computeHeading(
      point1LatLng,
      point2LatLng
    );
    const actualAngle = angle - 90;

    const marker = document.querySelector(`[src="${icon1.url}"]`);

    if (marker) {
      marker.style.transform = `rotate(${actualAngle}deg)`;
    }
  };

  let centerPathLat;
  let centerPathLng;
  if (paths && paths[0] && paths[0].lat != null && paths[0].lng != null) {
    centerPathLat = parseFloat(paths[0].lat);
    centerPathLng = parseFloat(paths[0].lng);
  } else {
    centerPathLat = centerLat;
    centerPathLng = centerLng;
  }

  return (
    <Card variant="outlined">
      {/* <div className="btnCont">
        <Button variant="contained" onClick={startSimulation}>
          Start Simulation
        </Button>
      </div> */}

      <div className="gMapCont">
        <GoogleMap
          defaultZoom={17}
          defaultCenter={{ lat: centerPathLat, lng: centerPathLng }}
          // center={{ lat: centerPathLat, lng: centerPathLng }}
          options={{
            scrollwheel: true,
            gestureHandling: "greedy",
          }}
        >
          <Polyline
            path={paths}
            options={{
              strokeColor: "#0088FF",
              strokeWeight: 6,
              strokeOpacity: 0.6,
              defaultVisible: true,
            }}
          />

          {stops.data.map((stop, index) => (
            <Marker
              key={index}
              position={{
                lat: stop.lat,
                lng: stop.lng,
              }}
              title={stop.id}
              label={`${index + 1}`}
            />
          ))}

          {progress && progress.length > 0 && (
            <>
              <Polyline path={progress} options={{ strokeColor: "orange" }} />
              <Marker icon={icon1} position={progress[progress.length - 1]} />
            </>
          )}

          {lastLocation && (
            <Marker
              icon={locationIcon}
              position={{
                lat: lastLocation.lat,
                lng: lastLocation.lng,
              }}
              title={`Current Position: Lat: ${lastLocation.lat}, Lng: ${
                lastLocation.lng
              }, Alt: ${lastLocation.alt}\nBattery: ${localStorage.getItem(
                "battery"
              )}%\nPower: ${localStorage.getItem("power")}`}
            />
          )}
        </GoogleMap>
      </div>
    </Card>
  );
};

export default withScriptjs(withGoogleMap(Map));
